"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { cn, toSafeErr } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import LabeledField from "./labeled-field";
import GoogleLoginButton from "../button/google-login-button";
import { PasswordInput } from "./password-input";
import { Input } from "../ui/input";
import { useAuthStore } from "@/stores/auth-store";

type FormErrors = Partial<
  Record<"name" | "email" | "password" | "confirmPassword" | "phone", string[]>
>;

export function SignupForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter();
  const user = useAuthStore((s) => s.user);

  const [phone, setPhone] = useState<string>("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      router.replace("/profile");
    }
  }, [user, router]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setMessage(null);

    const fd = new FormData(e.currentTarget);
    const body = Object.fromEntries(fd.entries());

    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // include cookies
        body: JSON.stringify(body),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (res.ok) {
        useAuthStore.getState().setUser(data.user);
        router.replace("/profile");
        return;
      }

      if (data?.errors) setErrors(data.errors);

      setMessage(data?.error || "Signup failed. Please check your inputs.");

      if (res.status === 409) {
        setErrors((prev) => ({ ...prev, email: ["Email already exists"] }));
      }
    } catch (err) {
      const safeError = toSafeErr(err);
      console.error(safeError);
      setMessage("Network error. Please try again.");
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Register</h2>
          </CardTitle>
          <CardDescription>
            Create a new account by filling out the form below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form noValidate onSubmit={onSubmit}>
            <div className="flex flex-col gap-5">
              <LabeledField
                label="Full Name"
                id="full-name"
                name="name"
                type="text"
                placeholder="John Doe"
                autoComplete="name"
                error={errors?.name}
                required
              />
              <LabeledField
                label="Email"
                id="email"
                name="email"
                type="email"
                placeholder="m@example.com"
                autoComplete="email"
                error={errors?.email}
                required
              />
              <LabeledField
                label="Phone Number"
                id="phone"
                error={errors?.phone}
                optional
              >
                <PhoneInputWithCountrySelect
                  international
                  id="phone"
                  name="phone"
                  defaultCountry="CA"
                  value={phone}
                  onChange={(v) => setPhone(v ? `${v}` : "")}
                  inputComponent={Input}
                  className="flex gap-2 ml-2"
                  placeholder="Enter your phone number"
                />
              </LabeledField>
              <LabeledField
                label="Password"
                id="password"
                error={errors?.password}
                description="Password must be at least 8 characters long and include at least one letter, one number, and one special character."
                required
              >
                <PasswordInput
                  placeholder="Enter your password"
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  required
                />
              </LabeledField>
              <LabeledField
                label="Confirm Password"
                id="confirm-password"
                error={errors?.confirmPassword}
                required
              >
                <PasswordInput
                  placeholder="Confirm your password"
                  id="confirm-password"
                  name="confirmPassword"
                  autoComplete="new-password-confirm"
                  required
                />
              </LabeledField>
              <Button type="submit" className="w-full mt-2">
                Register
              </Button>
            </div>
          </form>
          {message && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {message}
            </p>
          )}
          <div className="relative  mt-10 mb-6">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <span className="block w-full border-t border-gray-200"></span>
            </div>
            <div className="relative flex justify-center text-sm font-medium">
              <span className="bg-white px-6 text-gray-900">
                Or continue with
              </span>
            </div>
          </div>
          <GoogleLoginButton />
          <div className="mt-4 text-center text-sm">
            Already have an account?{" "}
            <Link href="/login" className="underline underline-offset-4">
              Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
